import { Injectable } from "@angular/core";
import { MsalService, MsalBroadcastService } from "@azure/msal-angular";
import {
  InteractionRequiredAuthError,
  AuthenticationResult,
  EventType,
  EventMessage,
} from "@azure/msal-browser";
import { filter, firstValueFrom, take, tap, map } from "rxjs";

import { environment } from "app/environments/environment";
import { LoggerService } from "../services/logger.service";

const TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000; // 5 minutes
const DEFAULT_TOKEN_LIFETIME_MS = 60 * 60 * 1000; // 1 hour (if expiresOn is not available)

@Injectable({ providedIn: "root" })
export class TokenService {
  private tokenCache: {
    token: string;
    expiry: number;
  } | null = null;

  private msalCoreInitialized = false;
  private readonly msalCoreInitializationPromise: Promise<void>;

  constructor(
    private readonly msalService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private logger: LoggerService
  ) {
    this.msalCoreInitializationPromise = firstValueFrom(
      this.msalBroadcastService.msalSubject$.pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.INITIALIZE_END
        ),
        take(1),
        tap(() => {
          this.msalCoreInitialized = true;
        }),
        map(() => undefined)
      )
    ).catch((error) => {
      this.msalCoreInitialized = false;
      throw new Error(
        "MSAL core failed to signal initialization end: " +
          (error instanceof Error ? error.message : String(error))
      );
    });
  }

  public async getToken(): Promise<string | null> {
    if (!this.msalCoreInitialized) {
      try {
        await this.msalCoreInitializationPromise;
        if (!this.msalCoreInitialized) {
          return null;
        }
      } catch (error) {
        return null;
      }
    }

    if (this.tokenCache && this.tokenCache.expiry > Date.now()) {
      this.logger.debug("TokenService", "Returning cached token");
      return this.tokenCache.token;
    }

    try {
      if (!this.msalService.instance) {
        return null;
      }

      const response = await this.acquireTokenSilent();
      if (response && response.accessToken) {
        this.tokenCache = {
          token: response.accessToken,
          expiry: response.expiresOn
            ? new Date(response.expiresOn).getTime() - TOKEN_EXPIRY_BUFFER_MS
            : Date.now() + DEFAULT_TOKEN_LIFETIME_MS - TOKEN_EXPIRY_BUFFER_MS,
        };
        return response.accessToken;
      }
      return null;
    } catch (error) {
      this.logger.error("TokenService", "Error acquiring token:", error);
      this.handleTokenError(error);
      return null;
    }
  }

  public removeToken(): void {
    if (this.msalService.instance) {
      this.msalService.instance.clearCache();
    }
    this.tokenCache = null;
    this.logger.info("TokenService", "Token removed from cache and memory");
  }

  private async acquireTokenSilent(): Promise<AuthenticationResult | null> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      this.logger.warn("TokenService", "No active account found");
      return null;
    }

    try {
      return await this.msalService.instance.acquireTokenSilent({
        scopes: environment.azureB2C.protectedResources.api.scopes,
        account,
      });
    } catch (error) {
      this.logger.warn(
        "TokenService",
        "Silent token acquisition failed:",
        error
      );
      throw error;
    }
  }

  private handleTokenError(error: unknown): void {
    if (error instanceof InteractionRequiredAuthError) {
      this.logger.warn(
        "TokenService",
        "Interaction required, redirecting to login"
      );
      this.msalService.loginRedirect({
        scopes: environment.azureB2C.protectedResources.api.scopes,
      });
    }
  }
}
